import { CarryValues, FundPerformanceOverview, NglTvpiForCarry, OverviewTotal } from './utils';
import { FundPerformance } from '../../../fund-performance/dto/FundPerformance';
import { FinCalc } from "../../../../services/financial-calculations/financial-calculations.service";

export class OrgOverviewCalcsService {
  static calcTotals(funds: FundPerformanceOverview[]): OverviewTotal {
    let totalCommitment = 0;
    let totalContribution = 0;
    const carriedInterest: CarryValues = {
      total: 0,
      realized: 0,
      unrealized: 0,
    };
    // Aggregates for BEFORE carry totals
    let beforeNgl = 0;
    let beforeDistribution = 0;
    let beforeTotalNav = 0;
    let beforeNavAndDistribution = 0;

    // Aggregates for AFTER carry totals
    let afterNgl = 0;
    let afterDistribution = 0;
    let afterTotalNav = 0;
    let afterNavAndDistribution = 0;

    funds.forEach((fund) => {
      const fp = fund.selectedFP;
      if (!fp) return;

      // Base values
      totalCommitment += fp.totalCommitment || 0;
      totalContribution += fp.contribution || 0;

      // Compute common values
      const netGain = FinCalc.netGain(fp.totalFmv, fp.totalCost, fp.totalExpenses);
      const absDistribution = Math.abs(fp.distribution) + Math.abs(fp.carryDistribution);
      const navValue = FinCalc.nav(
        fp.contribution,
        fp.distribution,
        fp.carryDistribution,
        fp.totalExpenses,
        fp.totalFmv,
        fp.totalCost
      );

      // Carry calculations
      fp.carryInterest = -FinCalc.carry(netGain, fp.carryInterestPercent);
      carriedInterest.total += fp.carryInterest || 0;
      carriedInterest.realized += -Math.abs(fp.carryDistribution || 0);
      carriedInterest.unrealized += ((fp.carryInterest || 0) - (-Math.abs(fp.carryDistribution) || 0));

      // BEFORE Carry calculations
      beforeNgl += netGain;
      beforeDistribution += absDistribution;
      beforeTotalNav += navValue;
      beforeNavAndDistribution += fp.navBeforeCarry + absDistribution;

      // AFTER Carry calculations
      const carry = FinCalc.carry(netGain, fp.carryInterestPercent);
      afterNgl += netGain - carry;
      afterDistribution += Math.abs(fp.distribution);
      const navAfterCarry =
        navValue - (carry + fp.carryDistribution);
      afterTotalNav += navAfterCarry;
      afterNavAndDistribution +=
        fp.navBeforeCarry +
        Math.abs(fp.distribution) +
        Math.abs(fp.carryDistribution) +
        fp.carryInterest;
    });

    // BEFORE carry metrics
    const beforeTvpi = totalContribution ? ((beforeNgl + totalContribution) / totalContribution) : 0;
    const beforeDpi = totalContribution ? (beforeDistribution / totalContribution) : 0;
    const beforeRvpi = totalContribution ? (beforeTotalNav / totalContribution) : 0;
    const beforeCarry: NglTvpiForCarry = {
      ngl: beforeNgl,
      navAndDistribution: beforeNavAndDistribution,
      distribution: beforeDistribution,
      tvpi: beforeTvpi,
      dpi: beforeDpi,
      rvpi: beforeRvpi,
    };

    // AFTER carry metrics
    const afterTvpi = totalContribution ? (afterNgl + totalContribution) / totalContribution : 0;
    const afterDpi = totalContribution ? afterDistribution / totalContribution : 0;
    const afterRvpi = totalContribution ? afterTotalNav / totalContribution : 0;
    const afterCarry: NglTvpiForCarry = {
      ngl: afterNgl,
      navAndDistribution: afterNavAndDistribution,
      distribution: afterDistribution,
      tvpi: afterTvpi,
      dpi: afterDpi,
      rvpi: afterRvpi,
    };

    return {
      totalCommitment,
      contributionReceivedPercent: totalCommitment ? totalContribution / totalCommitment : 0,
      contributionReceivedNumber: totalContribution,
      beforeCarry,
      afterCarry,
      carriedInterest,
    };
  }

  // These functions remain for individual FundPerformance calculations
  static calcBeforeCarry(fp: FundPerformance): NglTvpiForCarry {
    const nav = FinCalc.nav(fp.contribution, fp.distribution, fp.carryDistribution, fp.totalExpenses, fp.totalFmv, fp.totalCost);
    const totalDistribution = Math.abs(fp.distribution) + Math.abs(fp.carryDistribution);
    const ngl = fp.navBeforeCarry + totalDistribution - fp.contribution;
    const tvpi = fp.contribution
      ? (fp.navBeforeCarry + totalDistribution) / fp.contribution
      : 0;
    const dpi = FinCalc.dpi(totalDistribution, fp.contribution);
    const rvpi = FinCalc.rvpi(nav, fp.contribution);
    const navAndDistribution = fp.navBeforeCarry + totalDistribution;
    return { ngl, navAndDistribution, distribution: totalDistribution, tvpi, dpi, rvpi };
  }

  static calcAfterCarry(fp: FundPerformance): NglTvpiForCarry {
    const netGainLossBeforeCarry = FinCalc.netGain(fp.totalFmv, fp.totalCost, fp.totalExpenses);
    const carry = FinCalc.carry(netGainLossBeforeCarry, fp.carryInterestPercent);
    const ngl = netGainLossBeforeCarry - carry;
    const navAfterCarry = FinCalc.nav(
      fp.contribution,
      fp.distribution,
      fp.carryDistribution,
      fp.totalExpenses,
      fp.totalFmv,
      fp.totalCost
    ) - (carry + fp.carryDistribution);
    const distribution = Math.abs(fp.distribution);
    const tvpi = (ngl && fp.contribution)
      ? (ngl + fp.contribution) / fp.contribution
      : 0;
    const dpi = FinCalc.dpi(distribution, fp.contribution);
    const rvpi = FinCalc.rvpi(navAfterCarry, fp.contribution);
    const navAndDistribution = fp.navBeforeCarry + Math.abs(fp.distribution) + Math.abs(fp.carryDistribution) + fp.carryInterest;
    return { ngl, navAndDistribution, distribution, tvpi, dpi, rvpi };
  }

  static calcCarry(fp: FundPerformance): CarryValues {
    const total = fp.carryInterest;
    const realized = fp.carryDistribution;
    const unrealized = fp.carryInterest - fp.carryDistribution;
    return { total, realized, unrealized };
  }
}
